/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Formula } from '@gh/builder-shared-data';
import {
  combineReducers,
  composeSetters,
  fieldValidationSetter,
  onChangeReducer,
  removeDirtyRow,
  setDirty,
  setEntityField,
  setNewDirtyType,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { flatMap, isEqual, isNil, uniq } from 'lodash';
import {
  getJeComponentTopIds,
  getJeItemId,
  isJeComponentFieldAffectsPrice,
  JeComponent,
  jeQuantityResetSetter,
  validateJeComponentField,
} from '../../../resources/job-estimate-details.model';
import {
  jeComponentTable,
  JeDetailsState,
  JeDetailsStateEvent,
  JeDetailsStateEventType,
  jeItemTable,
  queryJeComponent,
  queryJeComponentItems,
} from '../je-details.state';
import { addOrUpdateCostCenters, markCostCentersAsDirty } from './je-details-cc.reducers';
import { cuttingListReducer, rootCuttingListReducer } from './je-details-cutting-list.reducers';
import { selectionConsistencyReducer } from './je-details-selection.reducers';
import { addOrUpdateTops, getModifiedTops } from './je-details-top.reducers';

export const setComponentField = composeSetters(
  setEntityField,
  jeQuantityResetSetter,
  setUpdatedDirtyType,
  fieldValidationSetter(validateJeComponentField));

export const jeDetailsComponentReducer = onChangeReducer(
  combineReducers({
    jobComponents: componentsReducer,
  }),
  componentItemsOnChangeReducer,
  costCenterComponentOnChangeReducer,
  formulaComponentOnChangeReducer,
  rootComponentOnChangeReducer,
  toOnChangeReducer(selectionConsistencyReducer),
);

/**
 * Marks specified component and its cost center as dirty
 */
export function markComponentAsDirty(state: JeDetailsState, componentId: ID): JeDetailsState {
  const dirtyComponentState = setEntityField(
    state,
    'jobComponents',
    jeComponentTable.mergeById(state.jobComponents, componentId, setUpdatedDirtyType));
  return markCostCentersAsDirty(dirtyComponentState, [state.jobComponentsCcId]);
}

function componentsReducer(state: Table<JeComponent>, event: JeDetailsStateEvent): Table<JeComponent> {
  switch (event.type) {
    case JeDetailsStateEventType.ComponentAddSet: {
      const { components } = event.componentSet;
      return jeComponentTable.insertAll(state, components.map(setNewDirtyType));
    }
    case JeDetailsStateEventType.ComponentSetField:
      return jeComponentTable.mergeById(
        state,
        event.componentId,
        (row: JeComponent) => setComponentField(row, event.name, event.value));
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemRemove:
    case JeDetailsStateEventType.CuttingListItemSetField:
      return jeComponentTable.mergeById(
        state,
        event.sourceId,
        (row: JeComponent) => componentReducer(row, event));
    case JeDetailsStateEventType.ComponentRemove:
      return jeComponentTable.mergeById(state, event.componentId, removeDirtyRow);
    default:
      return state;
  }
}

function componentReducer(component: JeComponent, event: JeDetailsStateEvent): JeComponent {
  switch (event.type) {
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemRemove:
    case JeDetailsStateEventType.CuttingListItemSetField:
      return setComponentField(component, 'cuttingList', cuttingListReducer(component.cuttingList || [], event));
    default:
      return component;
  }
}

function componentItemsOnChangeReducer(oldState: JeDetailsState,
                                       newState: JeDetailsState,
                                       event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.ComponentAddSet: {
      const { items } = event.componentSet;
      return setEntityField(newState, 'jobItems', jeItemTable.insertAll(newState.jobItems, items.map(setNewDirtyType)));
    }
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemRemove:
    case JeDetailsStateEventType.CuttingListItemSetField:
      return setEntityField(
        newState,
        'jobItems',
        jeItemTable.mergeAllById(
          oldState.jobItems,
          getComponentItemIds(oldState, event.sourceId),
          setUpdatedDirtyType));
    case JeDetailsStateEventType.ComponentSetField:
      return setEntityField(
        newState,
        'jobItems',
        jeItemTable.mergeAllById(
          oldState.jobItems,
          getComponentItemIds(oldState, event.componentId),
          setUpdatedDirtyType));
    case JeDetailsStateEventType.ComponentRemove:
      return setEntityField(
        newState,
        'jobItems',
        jeItemTable.mergeAllById(oldState.jobItems, getComponentItemIds(oldState, event.componentId), removeDirtyRow));
    default:
      return newState;
  }
}

function costCenterComponentOnChangeReducer(oldState: JeDetailsState,
                                            newState: JeDetailsState,
                                            event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.ComponentAddSet:
      return addOrUpdateCostCenters(newState, event.componentSet.affectedCostCenters, event.componentSet.newOrders);
    case JeDetailsStateEventType.ComponentSetField:
      return markCostCentersAsDirty(newState, getComponentCostCenterIds(newState, event.componentId));
    case JeDetailsStateEventType.ComponentRemove:
      return markCostCentersAsDirty(newState, getComponentCostCenterIds(oldState, event.componentId));
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemSetField:
    case JeDetailsStateEventType.CuttingListItemRemove:
      return markCostCentersAsDirty(newState, getComponentCostCenterIds(oldState, event.sourceId));
    default:
      return newState;
  }
}

function formulaComponentOnChangeReducer(oldState: JeDetailsState,
                                         newState: JeDetailsState,
                                         event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.ComponentAddSet:
      return addOrUpdateTops(newState, uniq(flatMap(event.componentSet.components, getJeComponentTopIds)));
    case JeDetailsStateEventType.ComponentSetField: {
      if (event.name === 'formula') {
        const formula: Formula = event.value;
        const component = <JeComponent>jeComponentTable.byId(newState.jobComponents, event.componentId);
        if (formula.valid && !isEqual(formula.takeOffParams, component.topIds)) {
          return addOrUpdateTops(
            setEntityField(
              newState,
              'jobComponents',
              jeComponentTable.update(newState.jobComponents, { ...component, topIds: formula.takeOffParams })),
            getModifiedTops(getJeComponentTopIds(component), formula.takeOffParams));
        }
      }
      return newState;
    }
    case JeDetailsStateEventType.ComponentRemove: {
      const component = <JeComponent>jeComponentTable.byId(oldState.jobComponents, event.componentId);
      return addOrUpdateTops(newState, getJeComponentTopIds(component));
    }
    default:
      return newState;
  }
}

function rootComponentOnChangeReducer(_: JeDetailsState,
                                      state: JeDetailsState,
                                      event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.ComponentAddSet:
    case JeDetailsStateEventType.ComponentRemove:
      return setRecalculationNeeded(setDirty(state));
    case JeDetailsStateEventType.ComponentSetField:
      if (isJeComponentFieldAffectsPrice(event.name)) {
        return setRecalculationNeeded(setDirty(state));
      } else {
        return setDirty(state);
      }
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemSetField:
    case JeDetailsStateEventType.CuttingListItemRemove:
      return rootCuttingListReducer(state, event);
    default:
      return state;
  }
}

function getComponentItemIds(state: JeDetailsState, componentId: ID): ID[] {
  const component = queryJeComponent(state, componentId);

  return isNil(component) ? [] : queryJeComponentItems(state, componentId).map(getJeItemId);
}

function getComponentCostCenterIds(state: JeDetailsState, componentId: ID): ID[] {
  const component = queryJeComponent(state, componentId);

  return isNil(component)
    ? []
    : uniq([state.jobComponentsCcId].concat(queryJeComponentItems(state, componentId)
      .map((item) => item.costCenterId)));
}
